"use client";

/** Converts the VAPID public key (base64url) into the byte array PushManager needs. */
export function urlBase64ToUint8Array(base64: string): Uint8Array {
  const padding = "=".repeat((4 - (base64.length % 4)) % 4);
  const normalized = (base64 + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(normalized);
  const output = new Uint8Array(raw.length);
  for (let index = 0; index < raw.length; index += 1) output[index] = raw.charCodeAt(index);
  return output;
}

export function pushSupport(): { supported: boolean; reason?: "no_service_worker" | "no_push_manager" | "no_notification" | "not_standalone_ios" } {
  if (typeof window === "undefined" || typeof navigator === "undefined") return { supported: false, reason: "no_service_worker" };
  if (!("serviceWorker" in navigator)) return { supported: false, reason: "no_service_worker" };
  // iOS Safari only exposes Web Push inside a Home Screen (standalone) app.
  const standalone = window.matchMedia?.("(display-mode: standalone)")?.matches || (navigator as unknown as { standalone?: boolean }).standalone === true;
  if (!("Notification" in window) && !standalone) return { supported: false, reason: "not_standalone_ios" };
  if (!("PushManager" in window)) return { supported: false, reason: "no_push_manager" };
  if (!("Notification" in window)) return { supported: false, reason: "no_notification" };
  return { supported: true };
}

export async function currentPushSubscription(): Promise<PushSubscription | null> {
  if (!pushSupport().supported) return null;
  try {
    const registration = await navigator.serviceWorker.ready;
    return await registration.pushManager.getSubscription();
  } catch {
    return null;
  }
}

/** Requests permission and creates (or reuses) the browser push subscription. */
export async function subscribeToPush(vapidPublicKey: string): Promise<PushSubscription> {
  if (!vapidPublicKey) throw new Error("VAPID公開鍵が未設定です");
  const support = pushSupport();
  if (!support.supported) {
    throw new Error(support.reason === "not_standalone_ios"
      ? "iPhoneでは「ホーム画面に追加」したアプリから開いてください"
      : "このブラウザはWeb Pushに対応していません");
  }
  const permission = Notification.permission === "granted" ? "granted" : await Notification.requestPermission();
  if (permission !== "granted") throw new Error("通知が許可されませんでした（ブラウザの設定を確認してください）");
  const registration = await navigator.serviceWorker.ready;
  const existing = await registration.pushManager.getSubscription();
  if (existing) return existing;
  return registration.pushManager.subscribe({
    userVisibleOnly: true,
    applicationServerKey: urlBase64ToUint8Array(vapidPublicKey) as unknown as BufferSource,
  });
}

export async function unsubscribeFromPush(): Promise<PushSubscription | null> {
  const subscription = await currentPushSubscription();
  if (!subscription) return null;
  await subscription.unsubscribe().catch(() => undefined);
  return subscription;
}
